import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { STATE_STYLE, STATE_ORDER } from "@/components/StateBadge";
import { Package, Users, Truck, Warehouse, TrendingUp, Boxes, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLiveEvents } from "@/lib/useLiveEvents";

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="bg-zinc-900 border border-white/10 p-6 hover:border-yellow-300/40 transition-colors">
      <div className="flex items-center justify-between mb-4">
        <Icon className="w-5 h-5 text-yellow-300" strokeWidth={1.5} />
        <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      </div>
      <div className="font-display font-black text-4xl md:text-5xl tracking-tighter">{value}</div>
      {sub && <div className="font-mono-tech text-xs text-zinc-500 mt-2">{sub}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);

  const reload = () => {
    api.get("/dashboard/admin").then((r) => setData(r.data));
    api.get("/orders?limit=8").then((r) => setRecent(r.data));
  };

  useEffect(() => { reload(); }, []);

  // Realtime updates
  useLiveEvents({
    onEvent: (evt) => {
      if (["order_created", "order_state_changed", "order_assigned", "order_scanned", "order_update"].includes(evt.type)) {
        reload();
      }
    },
  });

  return (
    <Layout>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-2">/ Dashboard</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Panel de control</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center gap-2 border border-emerald-500/30 bg-emerald-500/5 px-3 py-1.5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="font-mono-tech text-[10px] text-emerald-300 uppercase tracking-widest">Live</span>
          </div>
          <div className="font-mono-tech text-xs text-zinc-500 uppercase tracking-widest">
            {new Date().toLocaleDateString("es-CL", { weekday: "long", day: "numeric", month: "long" })}
          </div>
        </div>
      </div>

      {data && (
        <>
          {/* Leads pendientes highlight — visible solo si hay */}
          {data.pending_leads > 0 && (
            <Link
              to="/admin/leads"
              data-testid="leads-cta"
              className="mb-6 group flex items-center justify-between gap-6 border border-yellow-300/40 bg-yellow-300/5 hover:bg-yellow-300/10 p-5 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-yellow-300 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-black" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="font-mono-tech text-[10px] text-yellow-300 uppercase tracking-widest">/ Leads pendientes</div>
                  <div className="font-display font-black text-2xl tracking-tighter">
                    {data.pending_leads} {data.pending_leads === 1 ? "solicitud sin revisar" : "solicitudes sin revisar"}
                  </div>
                  <div className="text-xs text-zinc-400 mt-0.5">Revisá y convertí en clientes desde la bandeja de leads.</div>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-yellow-300 transition-transform group-hover:translate-x-1" />
            </Link>
          )}

          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <StatCard testid="stat-hoy-creados" icon={TrendingUp} label="Hoy" value={data.hoy_creados} sub="Pedidos creados" />
            <StatCard testid="stat-en-reparto" icon={Truck} label="En reparto" value={data.state_counts.en_reparto} sub="Activos" />
            <StatCard testid="stat-entregados-hoy" icon={Package} label="Entregados" value={data.hoy_entregados} sub="Hoy" />
            <StatCard testid="stat-lotes-abiertos" icon={Boxes} label="Lotes" value={data.lotes_abiertos} sub="Abiertos" />
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <StatCard testid="stat-clientes" icon={Users} label="Clientes" value={data.total_clientes} />
            <StatCard testid="stat-repartidores" icon={Truck} label="Repartidores" value={data.total_repartidores} />
            <StatCard testid="stat-total" icon={Warehouse} label="Total pedidos" value={data.total_pedidos} />
          </div>

          <div className="grid md:grid-cols-7 gap-px bg-white/10 mb-10">
            {STATE_ORDER.map((s) => (
              <div key={s} className="bg-zinc-950 p-4">
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{STATE_STYLE[s]?.label}</div>
                <div className="font-display font-black text-2xl">{data.state_counts[s] ?? 0}</div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="border border-white/10">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="font-mono-tech text-xs text-zinc-500 uppercase tracking-widest">/ Últimos pedidos</div>
          <Link to="/admin/pedidos" className="text-xs font-mono-tech uppercase tracking-widest text-yellow-300 hover:text-yellow-200" data-testid="link-todos-pedidos">Ver todos →</Link>
        </div>
        <table className="w-full">
          <thead className="bg-zinc-900">
            <tr className="text-left text-[10px] font-mono-tech uppercase tracking-widest text-zinc-500">
              <th className="px-4 py-3">Tracking</th>
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3">Destinatario</th>
              <th className="px-4 py-3">Comuna</th>
              <th className="px-4 py-3">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {recent.map((o) => (
              <tr key={o.id} className="hover:bg-zinc-900/60">
                <td className="px-4 py-3 font-mono-tech text-sm">{o.tracking_number}</td>
                <td className="px-4 py-3 text-sm">{o.cliente_name || "-"}</td>
                <td className="px-4 py-3 text-sm">{o.destinatario?.nombre}</td>
                <td className="px-4 py-3 text-sm text-zinc-400">{o.destinatario?.comuna}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 border text-xs font-mono-tech uppercase tracking-wider ${STATE_STYLE[o.state]?.color}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-current" />
                    {STATE_STYLE[o.state]?.label}
                  </span>
                </td>
              </tr>
            ))}
            {recent.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-zinc-500 font-mono-tech text-sm">Sin pedidos aún</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
